import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { getStreamingChatCompletion, getChatCompletion } from './aiIntegrations/chatCompletion';
import {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';


/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Parses CSV file and returns structured data.
 * @param {File} file - CSV file to parse.
 * @returns {Promise<Object>} Parsed data with headers and rows.
 */
export function parseCSVFile(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        resolve({
          data: results?.data,
          headers: results?.meta?.fields,
          rowCount: results?.data?.length
        });
      },
      error: (error) => {
        reject(new Error(`CSV parsing failed: ${error?.message}`));
      }
    });
  });
}

/**
 * Parses Excel file (xlsx, xls) and returns structured data.
 * Optimized for large files with multiple sheets - COMPLETE SYNCHRONOUS PROCESSING.
 * @param {File} file - Excel file to parse.
 * @param {Function} onProgress - Optional callback for progress updates (current sheet, total sheets).
 * @returns {Promise<Object>} Parsed data with headers and rows.
 */
export function parseExcelFile(file, onProgress = null) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        console.log('📊 Excel Parsing Started:', {
          fileName: file?.name,
          fileSize: file?.size,
          timestamp: new Date().toISOString()
        });
        
        const data = new Uint8Array(e?.target?.result);
        
        // Memory-efficient parsing options
        const workbook = XLSX.read(data, { 
          type: 'array',
          cellDates: true,
          cellNF: false,
          cellStyles: false,
          sheetStubs: false,
          dense: false
        });
        
        if (!workbook || !workbook.SheetNames || workbook.SheetNames.length === 0) {
          reject(new Error('Excel file contains no sheets'));
          return;
        }
        
        const totalSheets = workbook.SheetNames.length;
        console.log(`📄 Processing ${totalSheets} sheet(s)...`);
        
        // SYNCHRONOUS PROCESSING - No async delays
        // PASS 1: Collect all headers from all sheets
        let combinedHeaders = new Set();
        const sheetsData = [];
        
        for (let i = 0; i < totalSheets; i++) {
          const sheetName = workbook.SheetNames[i];
          const worksheet = workbook.Sheets[sheetName];
          
          if (!worksheet) continue;
          
          // Convert to JSON with header row
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          
          if (jsonData?.length > 0) {
            const sheetHeaders = jsonData[0];
            
            // Collect all unique headers
            if (Array.isArray(sheetHeaders)) {
              sheetHeaders.forEach(header => {
                if (header) combinedHeaders.add(String(header));
              });
            }
            
            // Store sheet data for second pass
            sheetsData.push({
              sheetName,
              headers: sheetHeaders,
              rows: jsonData.slice(1)
            });
            
            console.log(`  ✓ Sheet "${sheetName}": ${jsonData.length - 1} rows, ${sheetHeaders?.length} columns`);
          }
        }
        
        const allHeaders = Array.from(combinedHeaders);
        console.log(`📋 Total unique headers: ${allHeaders?.length}`);
        
        // PASS 2: Process all sheets with normalized headers - COMPLETE SYNCHRONOUS PROCESSING
        let allRows = [];
        let totalRowsProcessed = 0;
        
        for (let i = 0; i < sheetsData.length; i++) {
          const { sheetName, headers: sheetHeaders, rows: dataRows } = sheetsData[i];
          
          // Progress callback for UI updates
          if (onProgress) {
            onProgress(i + 1, totalSheets, sheetName);
          }
          
          // CRITICAL FIX: Process ALL rows synchronously without chunking or delays
          // Map ALL rows to objects using ALL headers
          const sheetRows = dataRows.map(row => {
            const obj = {};
            
            // Initialize all headers with empty string
            allHeaders.forEach(header => {
              obj[header] = '';
            });
            
            // Fill in values from current sheet
            sheetHeaders?.forEach((header, index) => {
              if (header && row[index] !== undefined) {
                obj[String(header)] = row[index];
              }
            });
            
            return obj;
          }).filter(row => {
            // Keep row if it has at least one non-empty value
            return Object.values(row)?.some(val => val !== null && val !== undefined && val !== '');
          });
          
          totalRowsProcessed += sheetRows?.length;
          
          // Add ALL rows from this sheet to combined data
          allRows = allRows.concat(sheetRows);
          
          console.log(`  ✓ Processed sheet "${sheetName}": ${sheetRows?.length} valid rows`);
        }
        
        if (allRows?.length === 0) {
          reject(new Error('Excel file is empty or contains no valid data'));
          return;
        }
        
        console.log('✅ Excel Parsing Complete:', {
          totalRows: allRows?.length,
          totalSheets: totalSheets,
          headers: allHeaders?.length,
          timestamp: new Date().toISOString()
        });
        
        // CRITICAL: Return ONLY after ALL data is fully processed
        resolve({
          data: allRows,
          headers: allHeaders,
          rowCount: allRows?.length,
          sheetCount: totalSheets
        });
      } catch (error) {
        console.error('❌ Excel parsing error:', error);
        if (error?.message?.includes('out of memory') || error?.message?.includes('allocation failed')) {
          reject(new Error(`File is too large for browser memory. Try splitting the file into smaller parts.`));
        } else {
          reject(new Error(`Excel parsing failed: ${error?.message}`));
        }
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read Excel file. The file may be corrupted.'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Generates accurate statistical summary from complete asset data.
 * @param {Array} csvData - Complete parsed CSV data.
 * @returns {string} Statistical summary of all assets.
 */
export function generateAssetStatistics(csvData) {
  const totalAssets = csvData?.length || 0;
  
  if (totalAssets === 0) {
    return '⚠️ No asset data found in the uploaded file.';
  }
  
  // CRITICAL FIX: Detect actual columns in the data
  const sampleRow = csvData?.[0] || {};
  const actualColumns = Object.keys(sampleRow);
  
  console.log('📋 Detected Asset Columns:', actualColumns);
  
  // Try to find type/category column with fuzzy matching
  const typeColumn = actualColumns?.find(col => 
    /type|category|equipment|class|group/i?.test(col)
  );
  
  const nameColumn = actualColumns?.find(col => 
    /name|asset|equipment|id|identifier/i?.test(col)
  );
  
  // Count assets by detected type column
  const assetTypes = {};
  let extractedCount = 0;
  
  csvData?.forEach(asset => {
    if (typeColumn && asset?.[typeColumn]) {
      const type = String(asset?.[typeColumn])?.trim();
      if (type && type !== '' && type?.toLowerCase() !== 'null' && type?.toLowerCase() !== 'undefined') {
        assetTypes[type] = (assetTypes?.[type] || 0) + 1;
        extractedCount++;
      }
    }
  });
  
  // Get top 5 asset types
  const topTypes = Object.entries(assetTypes)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.slice(0, 5)
    ?.map(([type, count]) => `${type}: ${count}`)
    ?.join(', ');
  
  let summary = `✅ **Complete Asset Data Extraction**\n\n`;
  summary += `📊 **Total Assets Processed:** ${totalAssets}\n\n`;
  summary += `📋 **Detected Columns:** ${actualColumns?.join(', ')}\n\n`;
  
  if (typeColumn && extractedCount > 0) {
    summary += `🔧 **Equipment Types (from "${typeColumn}" column):** ${topTypes}\n\n`;
    summary += `✓ Successfully extracted ${extractedCount} asset types from ${totalAssets} total assets.\n\n`;
  } else {
    summary += `⚠️ **No equipment type column detected.** Common column names: "type", "category", "equipment_type"\n\n`;
    summary += `✓ All ${totalAssets} asset rows have been saved, but type classification requires a type/category column.\n\n`;
  }
  
  if (nameColumn) {
    summary += `📝 **Asset names found in column:** "${nameColumn}"\n\n`;
  }
  
  summary += `You can now ask questions about any asset, equipment, or maintenance patterns.`;
  
  return summary;
}

/**
 * Generates accurate statistical summary from complete work order data.
 * @param {Array} csvData - Complete parsed CSV data.
 * @returns {string} Statistical summary of all work orders.
 */
export function generateWorkOrderStatistics(csvData) {
  const totalWorkOrders = csvData?.length || 0;
  
  if (totalWorkOrders === 0) {
    return '⚠️ No work order data found in the uploaded file.';
  }
  
  // CRITICAL FIX: Detect actual columns in the data
  const sampleRow = csvData?.[0] || {};
  const actualColumns = Object.keys(sampleRow);
  
  console.log('📋 Detected Work Order Columns:', actualColumns);
  
  // Try to find issue/problem column with fuzzy matching
  const issueColumn = actualColumns?.find(col => 
    /issue|problem|fault|failure|defect|breakdown|complaint/i?.test(col)
  );
  
  const statusColumn = actualColumns?.find(col => 
    /status|state|condition/i?.test(col)
  );
  
  const downtimeColumn = actualColumns?.find(col => 
    /downtime|duration|time|hours|minutes/i?.test(col)
  );
  
  const equipmentColumn = actualColumns?.find(col => 
    /equipment|asset|machine|device/i?.test(col)
  );
  
  // Count work orders by detected issue column
  const issueTypes = {};
  const statuses = {};
  let totalDowntime = 0;
  let downtimeCount = 0;
  let extractedIssueCount = 0;
  
  csvData?.forEach(wo => {
    // Extract issue type from detected column
    if (issueColumn && wo?.[issueColumn]) {
      const issue = String(wo?.[issueColumn])?.trim();
      if (issue && issue !== '' && issue?.toLowerCase() !== 'null' && issue?.toLowerCase() !== 'undefined') {
        issueTypes[issue] = (issueTypes?.[issue] || 0) + 1;
        extractedIssueCount++;
      }
    }
    
    // Extract status from detected column
    if (statusColumn && wo?.[statusColumn]) {
      const status = String(wo?.[statusColumn])?.trim();
      if (status && status !== '') {
        statuses[status] = (statuses?.[status] || 0) + 1;
      }
    }
    
    // Extract downtime from detected column
    if (downtimeColumn && wo?.[downtimeColumn]) {
      const downtime = parseFloat(wo?.[downtimeColumn]);
      if (!isNaN(downtime) && downtime > 0) {
        totalDowntime += downtime;
        downtimeCount++;
      }
    }
  });
  
  // Get top 5 issue types
  const topIssues = Object.entries(issueTypes)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.slice(0, 5)
    ?.map(([issue, count]) => `${issue}: ${count}`)
    ?.join(', ');
  
  const avgDowntime = downtimeCount > 0 ? (totalDowntime / downtimeCount)?.toFixed(1) : 0;
  
  let summary = `✅ **Complete Work Order Data Extraction**\n\n`;
  summary += `📊 **Total Work Orders Processed:** ${totalWorkOrders}\n\n`;
  summary += `📋 **Detected Columns:** ${actualColumns?.join(', ')}\n\n`;
  
  if (issueColumn && extractedIssueCount > 0) {
    summary += `⚠️ **Top Issues (from "${issueColumn}" column):** ${topIssues}\n\n`;
    summary += `✓ Successfully extracted ${extractedIssueCount} issue types from ${totalWorkOrders} total work orders.\n\n`;
  } else {
    summary += `⚠️ **No issue/problem column detected.** Common column names: "issue", "problem", "fault", "failure"\n\n`;
    summary += `✓ All ${totalWorkOrders} work order rows have been saved, but issue classification requires an issue/problem column.\n\n`;
  }
  
  if (equipmentColumn) {
    summary += `🔧 **Equipment data found in column:** "${equipmentColumn}"\n\n`;
  }
  
  if (downtimeColumn && downtimeCount > 0) {
    summary += `⏱️ **Average Downtime (from "${downtimeColumn}" column):** ${avgDowntime} minutes (from ${downtimeCount} records)\n\n`;
  }
  
  if (statusColumn && Object.keys(statuses)?.length > 0) {
    const topStatuses = Object.entries(statuses)
      ?.sort((a, b) => b?.[1] - a?.[1])
      ?.slice(0, 3)
      ?.map(([status, count]) => `${status}: ${count}`)
      ?.join(', ');
    summary += `📈 **Work Order Status (from "${statusColumn}" column):** ${topStatuses}\n\n`;
  }
  
  summary += `You can now ask about breakdown patterns, recurring issues, or maintenance trends.`;
  
  return summary;
}

/**
 * Analyzes asset list CSV data using OpenAI with short summary.
 * @param {Array} csvData - Parsed CSV data.
 * @param {Function} onChunk - Callback for streaming response.
 */
export async function analyzeAssetData(csvData, onChunk) {
  try {
    // Reduce preview size to 20 rows to speed up processing
    const dataPreview = csvData?.slice(0, 20)?.map(row => JSON.stringify(row))?.join('\n');
    const totalRows = csvData?.length;

    const messages = [
      {
        role: 'system',
        content: `You are an expert maintenance analytics assistant. Provide a brief, concise summary of asset data in 2-3 sentences maximum. Focus only on key highlights.`
      },
      {
        role: 'user',
        content: `Analyze this asset list data (${totalRows} total assets, showing first 20):\n\n${dataPreview}\n\nProvide a brief summary (2-3 sentences) highlighting only the most critical insights.`
      }
    ];

    // Add timeout promise to prevent indefinite hang
    const analysisPromise = new Promise((resolve, reject) => {
      let completed = false;
      
      const timeoutId = setTimeout(() => {
        if (!completed) {
          reject(new Error('Analysis timed out after 30 seconds'));
        }
      }, 30000);

      getStreamingChatCompletion(
        'OPEN_AI',
        'gpt-4o-mini',
        messages,
        (chunk) => {
          const content = chunk?.choices?.[0]?.delta?.content || '';
          if (content) {
            onChunk(content);
          }
        },
        () => {
          completed = true;
          clearTimeout(timeoutId);
          resolve();
        },
        (error) => {
          completed = true;
          clearTimeout(timeoutId);
          reject(error);
        }
      );
    });

    await analysisPromise;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error analyzing asset data:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes work order log CSV data using OpenAI with short summary.
 * @param {Array} csvData - Parsed CSV data.
 * @param {Function} onChunk - Callback for streaming response.
 */
export async function analyzeWorkOrderData(csvData, onChunk) {
  try {
    // Reduce preview size to 20 rows to speed up processing
    const dataPreview = csvData?.slice(0, 20)?.map(row => JSON.stringify(row))?.join('\n');
    const totalRows = csvData?.length;

    const messages = [
      {
        role: 'system',
        content: `You are an expert maintenance analytics assistant. Provide a brief, concise summary of work order data in 2-3 sentences maximum. Focus only on key highlights.`
      },
      {
        role: 'user',
        content: `Analyze this work order log data (${totalRows} total work orders, showing first 20):\n\n${dataPreview}\n\nProvide a brief summary (2-3 sentences) highlighting only the most critical insights.`
      }
    ];

    // Add timeout promise to prevent indefinite hang
    const analysisPromise = new Promise((resolve, reject) => {
      let completed = false;
      
      const timeoutId = setTimeout(() => {
        if (!completed) {
          reject(new Error('Analysis timed out after 30 seconds'));
        }
      }, 30000);

      getStreamingChatCompletion(
        'OPEN_AI',
        'gpt-4o-mini',
        messages,
        (chunk) => {
          const content = chunk?.choices?.[0]?.delta?.content || '';
          if (content) {
            onChunk(content);
          }
        },
        () => {
          completed = true;
          clearTimeout(timeoutId);
          resolve();
        },
        (error) => {
          completed = true;
          clearTimeout(timeoutId);
          reject(error);
        }
      );
    });

    await analysisPromise;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error analyzing work order data:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Generates analytics and insights from combined asset and work order data.
 * @param {Array} assetData - Parsed asset CSV data.
 * @param {Array} workOrderData - Parsed work order CSV data.
 * @returns {Promise<Object>} Structured analytics data.
 */
export async function generateCombinedAnalytics(assetData, workOrderData) {
  try {
    const assetPreview = assetData?.slice(0, 20)?.map(row => JSON.stringify(row))?.join('\n');
    const workOrderPreview = workOrderData?.slice(0, 20)?.map(row => JSON.stringify(row))?.join('\n');

    const messages = [
      {
        role: 'system',
        content: 'You are an expert maintenance analytics assistant. Generate structured analytics data for visualization.'
      },
      {
        role: 'user',
        content: `Based on this asset data:\n${assetPreview}\n\nAnd work order data:\n${workOrderPreview}\n\nGenerate analytics including KPIs, risk scores, and heat map data.`
      }
    ];

    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4o-mini',
      messages,
      {
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'analytics_data',
            schema: {
              type: 'object',
              properties: {
                kpis: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      title: { type: 'string' },
                      value: { type: 'string' },
                      change: { type: 'string' },
                      trend: { type: 'string' }
                    }
                  }
                },
                riskAssets: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      assetName: { type: 'string' },
                      riskScore: { type: 'number' },
                      riskLevel: { type: 'string' },
                      issues: { type: 'string' }
                    }
                  }
                },
                insights: {
                  type: 'array',
                  items: { type: 'string' }
                }
              },
              required: ['kpis', 'riskAssets', 'insights'],
              additionalProperties: false
            }
          }
        }
      }
    );

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating combined analytics:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Save asset data to localStorage with shared key (accessible to all users)
 */
export function saveAssetData(data) {
  try {
    const storageKey = 'clarity_asset_data_shared';
    localStorage.setItem(storageKey, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving asset data:', error);
    return false;
  }
}

/**
 * Load asset data from localStorage with shared key (accessible to all users)
 */
export function loadAssetData() {
  try {
    const storageKey = 'clarity_asset_data_shared';
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading asset data:', error);
    return [];
  }
}

/**
 * Add a new asset to saved data
 */
export function addAsset(asset) {
  const currentData = loadAssetData();
  const updatedData = [...currentData, asset];
  saveAssetData(updatedData);
  return updatedData;
}

/**
 * Remove an asset from saved data by matching criteria
 */
export function removeAsset(criteria) {
  const currentData = loadAssetData();
  const updatedData = currentData?.filter(asset => {
    return !Object.keys(criteria)?.every(key => 
      asset?.[key]?.toString()?.toLowerCase() === criteria?.[key]?.toString()?.toLowerCase()
    );
  });
  saveAssetData(updatedData);
  return updatedData;
}

/**
 * Save work order data to localStorage with shared key (accessible to all users)
 */
export function saveWorkOrderData(data) {
  try {
    const storageKey = 'clarity_workorder_data_shared';
    localStorage.setItem(storageKey, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving work order data:', error);
    return false;
  }
}

/**
 * Load work order data from localStorage with shared key (accessible to all users)
 */
export function loadWorkOrderData() {
  try {
    const storageKey = 'clarity_workorder_data_shared';
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading work order data:', error);
    return [];
  }
}

/**
 * Clear asset data from localStorage for current user
 */
export function clearAssetData() {
  try {
    const storageKey = 'clarity_asset_data_shared';
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Error clearing asset data:', error);
    return false;
  }
}

/**
 * Clear work order data from localStorage for current user
 */
export function clearWorkOrderData() {
  try {
    const storageKey = 'clarity_workorder_data_shared';
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    console.error('Error clearing work order data:', error);
    return false;
  }
}

/**
 * Clear all data (assets and work orders) for current user
 */
export function clearAllData() {
  const assetCleared = clearAssetData();
  const workOrderCleared = clearWorkOrderData();
  return assetCleared && workOrderCleared;
}

/**
 * Analyzes historical data to extract key insights for AI suggestions.
 * @param {Array} assetData - Asset data from localStorage.
 * @param {Array} workOrderData - Work order data from localStorage.
 * @returns {Object} Structured insights about equipment, failures, and maintenance patterns.
 */
export function extractHistoricalInsights(assetData, workOrderData) {
  const insights = {
    totalAssets: assetData?.length || 0,
    totalWorkOrders: workOrderData?.length || 0,
    assetTypes: {},
    failurePatterns: {},
    maintenanceFrequency: {},
    downtimeData: [],
    criticalEquipment: [],
    commonIssues: {}
  };

  // Analyze asset types and criticality
  assetData?.forEach(asset => {
    const type = asset?.type || asset?.category || asset?.equipment_type || 'Unknown';
    insights.assetTypes[type] = (insights?.assetTypes?.[type] || 0) + 1;
    
    // Identify critical equipment based on various field names
    const isCritical = 
      asset?.critical === 'Yes' || 
      asset?.critical === true || 
      asset?.criticality === 'High' ||
      asset?.priority === 'Critical' ||
      asset?.importance === 'High';
    
    if (isCritical) {
      insights?.criticalEquipment?.push({
        name: asset?.name || asset?.asset_name || asset?.equipment_name || 'Unknown',
        type: type
      });
    }
  });

  // Analyze work orders for failure patterns and downtime
  workOrderData?.forEach(wo => {
    // Extract failure/issue type
    const issueType = 
      wo?.issue_type || 
      wo?.failure_type || 
      wo?.problem_type || 
      wo?.category || 
      wo?.type || 
      'General Maintenance';
    
    insights.commonIssues[issueType] = (insights?.commonIssues?.[issueType] || 0) + 1;
    
    // Extract equipment/asset name
    const equipmentName = 
      wo?.equipment || 
      wo?.asset || 
      wo?.asset_name || 
      wo?.equipment_name || 
      wo?.machine || 
      'Unknown';
    
    insights.failurePatterns[equipmentName] = (insights?.failurePatterns?.[equipmentName] || 0) + 1;
    
    // Track maintenance frequency
    const maintenanceType = 
      wo?.maintenance_type || 
      wo?.work_type || 
      wo?.type || 
      'Corrective';
    
    insights.maintenanceFrequency[maintenanceType] = (insights?.maintenanceFrequency?.[maintenanceType] || 0) + 1;
    
    // Extract downtime data
    const downtime = 
      wo?.downtime || 
      wo?.downtime_hours || 
      wo?.duration || 
      wo?.time_spent || 
      0;
    
    if (downtime > 0) {
      insights?.downtimeData?.push({
        equipment: equipmentName,
        downtime: parseFloat(downtime),
        issue: issueType
      });
    }
  });

  return insights;
}
